import React, { useEffect, useState } from "react";
import {
  Box, Typography, Button, Chip, CircularProgress, alpha, TextField,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Dialog, DialogContent, Divider, IconButton,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InventoryIcon from "@mui/icons-material/Inventory";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PageLoader from "../../Components/Loader/PageLoader";
import { useNotifier } from "../../Core/Notifier";
import dayjs from "dayjs";

const API_URL = (process.env.REACT_APP_API_URL || "").trim();
const ACCENT = "#00838f";
const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  draft: { label: "Draft", color: "#64748b", bg: "#f1f5f9" },
  sent: { label: "Sent", color: "#3b82f6", bg: "#eff6ff" },
  confirmed: { label: "Confirmed", color: "#7c3aed", bg: "#f5f3ff" },
  partially_received: { label: "Partially Received", color: "#ea580c", bg: "#fff7ed" },
  received: { label: "Received", color: "#059669", bg: "#ecfdf5" },
  cancelled: { label: "Cancelled", color: "#dc2626", bg: "#fef2f2" },
};

const PurchaseOrderDetail: React.FC = () => {
  const { id: rawId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();
  const [po, setPO] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [grnOpen, setGrnOpen] = useState(false);
  const [grnItems, setGrnItems] = useState<{ product_id: number; productName: string; ordered: number; received_qty: string; damaged_qty: string }[]>([]);
  const [receiving, setReceiving] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);

  // Detect if this is a legacy bill PO (id starts with "bill-")
  const isLegacyBill = rawId?.startsWith("bill-") || false;
  const id = isLegacyBill ? rawId!.replace("bill-", "") : rawId;

  const getHeaders = () => ({
    "Content-Type": "application/json", Accept: "application/json",
    Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
  });

  const loadPO = async () => {
    setLoading(true);
    try {
      if (isLegacyBill) {
        // Load from bill table
        const res = await fetch(`${API_URL}/bill/getAll`, {
          method: "POST", headers: getHeaders(),
          body: JSON.stringify({ searchText: "", id }),
        });
        const json = await res.json();
        const bills = json?.data || [];
        const bill = Array.isArray(bills) ? bills.find((b: any) => String(b.id) === String(id)) : bills;
        if (bill) {
          const statusMap: Record<number, string> = { 1: "draft", 2: "sent", 3: "confirmed", 4: "received", 5: "cancelled" };
          const items = Array.isArray(bill.quoteWorkOrderSale) ? bill.quoteWorkOrderSale : JSON.parse(bill.quoteWorkOrderSale || "[]");
          setPO({
            id: bill.id,
            po_number: bill.quoteWorkOrderNo,
            supplier_id: bill.quoteWorkOrderCustomerId,
            supplier_name: bill.customerName || bill.customerCompanyName || `Supplier #${bill.quoteWorkOrderCustomerId}`,
            supplier_email: "",
            supplier_phone: "",
            order_date: bill.quoteWorkOrderIssueDate,
            status: bill.receiving_status || statusMap[bill.mainStatus] || "draft",
            total_amount: bill.quoteWorkOrderSaleGrandTotal,
            source: "manual",
            demand_date: null,
            notes: bill.quoteWorkOrderCustomerRemark || null,
            created_at: bill.created_at,
            items: items.map((item: any) => ({
              product_id: item.productId,
              productName: item.productName,
              quantity: item.quantity,
              unit: "",
              unit_price: item.price,
              total: (item.quantity || 0) * (item.price || 0),
            })),
            grn_history: [],
            table_source: "bill",
          });
        }
      } else {
        const res = await fetch(`${API_URL}/procurement/purchase-orders/${id}`, { headers: getHeaders() });
        const json = await res.json();
        if (json.status) setPO({ ...json.data, table_source: "purchase_orders" });
      }
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { loadPO(); }, [id]);

  const updateStatus = async (status: string) => {
    setStatusUpdating(true);
    try {
      if (isLegacyBill) {
        // Update bill receiving_status
        await fetch(`${API_URL}/bill/update-receiving-status`, {
          method: "POST", headers: getHeaders(),
          body: JSON.stringify({ id, receiving_status: status }),
        });
      } else {
        await fetch(`${API_URL}/procurement/purchase-orders/${id}/status`, { method: "PUT", headers: getHeaders(), body: JSON.stringify({ status }) });
      }
      showMessage(`Status updated to ${status}`);
      loadPO();
    } catch { showErrorMessage("Failed to update status"); }
    finally { setStatusUpdating(false); }
  };

  const openGRN = () => {
    if (!po) return;
    setGrnItems(po.items.map((item: any) => ({
      product_id: item.product_id, productName: item.productName,
      ordered: Number(item.quantity), received_qty: String(item.quantity), damaged_qty: "0",
    })));
    setGrnOpen(true);
  };

  const handleReceive = async () => {
    setReceiving(true);
    try {
      const endpoint = isLegacyBill
        ? `${API_URL}/bill/${id}/receive`
        : `${API_URL}/procurement/purchase-orders/${id}/receive`;
      const res = await fetch(endpoint, {
        method: "POST", headers: getHeaders(),
        body: JSON.stringify({
          items: grnItems.map(i => ({
            product_id: i.product_id,
            received_qty: Number(i.received_qty) || 0,
            damaged_qty: Number(i.damaged_qty) || 0,
          })),
        }),
      });
      const json = await res.json();
      if (json.status) {
        showMessage(json.message);
        setGrnOpen(false);
        loadPO();
      } else showErrorMessage(json.message);
    } catch { showErrorMessage("Failed to receive goods"); }
    finally { setReceiving(false); }
  };

  if (loading) return <PageLoader />;
  if (!po) return <Box sx={{ textAlign: "center", py: 8 }}><Typography color="#94a3b8">Purchase order not found</Typography></Box>;

  // Normalize legacy status values
  const normalizedStatus = (() => {
    const s = (po.status || "").toLowerCase();
    if (s === "pending" || s === "1") return "draft";
    if (s === "completed" || s === "received") return "received";
    if (s === "partial") return "partially_received";
    return STATUS_CONFIG[s] ? s : "draft";
  })();

  const sc = STATUS_CONFIG[normalizedStatus] || STATUS_CONFIG.draft;
  const canReceive = ["draft", "sent", "confirmed", "partially_received"].includes(normalizedStatus);
  const canSend = normalizedStatus === "draft";

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <IconButton onClick={() => navigate("/procurement/purchase-orders")} sx={{ border: "1px solid #e2e8f0", borderRadius: 2 }}>
          <ArrowBackIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography fontSize={22} fontWeight={800} color="#0f172a" sx={{ fontFamily: "monospace" }}>{po.po_number}</Typography>
            <Chip label={sc.label} size="small" sx={{ height: 24, fontSize: 11, fontWeight: 700, bgcolor: sc.bg, color: sc.color, border: `1px solid ${alpha(sc.color, 0.15)}` }} />
            {isLegacyBill && <Chip label="Legacy PO" size="small" sx={{ height: 20, fontSize: 9, fontWeight: 600, bgcolor: "#f1f5f9", color: "#64748b" }} />}
            {po.source === "auto_demand" && <Chip label="Auto-generated" size="small" sx={{ height: 20, fontSize: 9, fontWeight: 700, bgcolor: "#f5f3ff", color: "#7c3aed" }} />}
          </Box>
          <Typography fontSize={12} color="#94a3b8">{po.supplier_name} · {dayjs(po.order_date).format("MMM D, YYYY")}</Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          {canSend && (
            <Button size="small" variant="outlined" startIcon={statusUpdating ? <CircularProgress size={14} /> : <SendIcon sx={{ fontSize: 16 }} />}
              onClick={() => updateStatus("sent")} disabled={statusUpdating}
              sx={{ borderRadius: 2.5, textTransform: "none", fontSize: 12, fontWeight: 700, borderColor: "#3b82f6", color: "#3b82f6" }}>
              Mark as Sent
            </Button>
          )}
          {canReceive && (
            <Button size="small" variant="contained" disableElevation startIcon={<InventoryIcon sx={{ fontSize: 16 }} />}
              onClick={openGRN}
              sx={{ borderRadius: 2.5, textTransform: "none", fontSize: 12, fontWeight: 700, bgcolor: "#059669", "&:hover": { bgcolor: "#047857" } }}>
              Receive Goods
            </Button>
          )}
          {normalizedStatus !== "cancelled" && normalizedStatus !== "received" && (
            <Button size="small" variant="outlined" onClick={() => updateStatus("cancelled")} disabled={statusUpdating}
              sx={{ borderRadius: 2.5, textTransform: "none", fontSize: 12, fontWeight: 600, borderColor: "#fecaca", color: "#dc2626" }}>
              Cancel
            </Button>
          )}
        </Box>
      </Box>

      {/* Info Cards */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        {[
          { label: "Supplier", value: po.supplier_name },
          { label: "Total", value: `$${Number(po.total_amount).toFixed(2)}` },
          { label: "Items", value: po.items?.length || 0 },
          { label: "Order Date", value: dayjs(po.order_date).format("MMM D, YYYY") },
        ].map((card, i) => (
          <Box key={i} sx={{ flex: "1 1 0", minWidth: 140, p: 2, borderRadius: 3, bgcolor: "#fff", border: "1px solid #e2e8f0" }}>
            <Typography fontSize={10} fontWeight={600} color="#94a3b8" textTransform="uppercase">{card.label}</Typography>
            <Typography fontSize={16} fontWeight={800} color="#0f172a" mt={0.5}>{card.value}</Typography>
          </Box>
        ))}
      </Box>

      {/* Items Table */}
      <Box sx={{ bgcolor: "#fff", borderRadius: 3, border: "1px solid #e2e8f0", overflow: "hidden", mb: 3 }}>
        <Box sx={{ px: 2.5, py: 1.5, borderBottom: "1px solid #e2e8f0" }}>
          <Typography fontSize={14} fontWeight={700} color="#0f172a">Order Items</Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#f8fafc" }}>
                {["Product", "Unit", "Qty", "Unit Price", "Total"].map(h => (
                  <TableCell key={h} sx={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", py: 1.5, textTransform: "uppercase" }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {(po.items || []).map((item: any, i: number) => (
                <TableRow key={i}>
                  <TableCell sx={{ py: 1.5 }}><Typography fontSize={13} fontWeight={500}>{item.productName}</Typography></TableCell>
                  <TableCell sx={{ py: 1.5 }}><Chip label={item.unit || item.unit_abbr || "-"} size="small" sx={{ height: 20, fontSize: 10, fontWeight: 600, bgcolor: "#f1f5f9", color: "#64748b" }} /></TableCell>
                  <TableCell sx={{ py: 1.5 }}><Typography fontSize={13} fontWeight={700}>{Number(item.quantity)}</Typography></TableCell>
                  <TableCell sx={{ py: 1.5 }}><Typography fontSize={12} color="#64748b">${Number(item.unit_price).toFixed(2)}</Typography></TableCell>
                  <TableCell sx={{ py: 1.5 }}><Typography fontSize={13} fontWeight={700} color="#059669">${Number(item.total).toFixed(2)}</Typography></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* GRN History */}
      {po.grn_history?.length > 0 && (
        <Box sx={{ bgcolor: "#fff", borderRadius: 3, border: "1px solid #e2e8f0", overflow: "hidden" }}>
          <Box sx={{ px: 2.5, py: 1.5, borderBottom: "1px solid #e2e8f0" }}>
            <Typography fontSize={14} fontWeight={700} color="#0f172a">Receiving History</Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f8fafc" }}>
                  {["GRN #", "Product", "Ordered", "Received", "Damaged", "Date"].map(h => (
                    <TableCell key={h} sx={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", py: 1, textTransform: "uppercase" }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {po.grn_history.map((grn: any, i: number) => (
                  <TableRow key={i}>
                    <TableCell sx={{ py: 1, fontSize: 11, fontFamily: "monospace" }}>{grn.grn_number}</TableCell>
                    <TableCell sx={{ py: 1, fontSize: 11 }}>{grn.product_id}</TableCell>
                    <TableCell sx={{ py: 1, fontSize: 11 }}>{Number(grn.ordered_qty)}</TableCell>
                    <TableCell sx={{ py: 1, fontSize: 11, fontWeight: 700, color: "#059669" }}>{Number(grn.received_qty)}</TableCell>
                    <TableCell sx={{ py: 1, fontSize: 11, color: Number(grn.damaged_qty) > 0 ? "#dc2626" : "#94a3b8" }}>{Number(grn.damaged_qty)}</TableCell>
                    <TableCell sx={{ py: 1, fontSize: 10, color: "#94a3b8" }}>{dayjs(grn.created_at).format("MMM D, h:mm A")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* ═══ GRN DIALOG ═══ */}
      <Dialog open={grnOpen} onClose={() => setGrnOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
        <Box sx={{ background: "linear-gradient(135deg, #059669, #047857)", px: 3, py: 2.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <InventoryIcon sx={{ fontSize: 22, color: "#fff" }} />
            <Box>
              <Typography fontSize={16} fontWeight={700} color="#fff">Receive Goods</Typography>
              <Typography fontSize={11} color="rgba(255,255,255,0.6)">{po.po_number} · {po.supplier_name}</Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={() => setGrnOpen(false)} sx={{ color: "rgba(255,255,255,0.5)" }}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
        </Box>
        <DialogContent sx={{ p: 0 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "#f8fafc" }}>
                  {["Product", "Ordered", "Received", "Damaged"].map(h => (
                    <TableCell key={h} sx={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", py: 1.5, textTransform: "uppercase" }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {grnItems.map((item, i) => (
                  <TableRow key={i}>
                    <TableCell sx={{ py: 1.5 }}><Typography fontSize={12} fontWeight={500}>{item.productName}</Typography></TableCell>
                    <TableCell sx={{ py: 1.5 }}><Typography fontSize={12} fontWeight={700}>{item.ordered}</Typography></TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <TextField size="small" type="number" value={item.received_qty}
                        onChange={e => { const n = [...grnItems]; n[i].received_qty = e.target.value; setGrnItems(n); }}
                        inputProps={{ min: 0, step: 1 }}
                        sx={{ width: 80, "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: 13, "& fieldset": { borderColor: "#e2e8f0" } } }} />
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <TextField size="small" type="number" value={item.damaged_qty}
                        onChange={e => { const n = [...grnItems]; n[i].damaged_qty = e.target.value; setGrnItems(n); }}
                        inputProps={{ min: 0, step: 1 }}
                        sx={{ width: 80, "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: 13, "& fieldset": { borderColor: "#e2e8f0" } } }} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Divider />
          <Box sx={{ display: "flex", gap: 1.5, p: 2.5, justifyContent: "flex-end" }}>
            <Button variant="outlined" onClick={() => setGrnOpen(false)}
              sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 600, borderColor: "#e2e8f0", color: "#64748b" }}>Cancel</Button>
            <Button variant="contained" disableElevation onClick={handleReceive} disabled={receiving}
              startIcon={receiving ? <CircularProgress size={14} color="inherit" /> : <CheckCircleIcon sx={{ fontSize: 16 }} />}
              sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 700, bgcolor: "#059669", "&:hover": { bgcolor: "#047857" } }}>
              {receiving ? "Processing..." : "Confirm Receipt"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default PurchaseOrderDetail;
